import { useNavigate } from "react-router-dom";
import {
  DEFAULT_TEACH_IN_BRIEF,
  EXPERIENCES,
  normalizeTeachInBrief,
  TEACH_IN_BRIEF_STORAGE_KEY,
  TeachInBrief,
} from "../../config/earthDayExperience";
import "./resume-upload.scss";
import { useState } from "react";

const FIELD_NOTES = [
  "Middle-school Earth Day club",
  "Library community workshop",
  "Neighborhood clean-energy teach-in",
  "Campus climate justice session",
];

const ResumeUpload = () => {
  const [brief, setBrief] = useState<TeachInBrief>(() => {
    try {
      const saved = localStorage.getItem(TEACH_IN_BRIEF_STORAGE_KEY);
      return saved
        ? normalizeTeachInBrief(JSON.parse(saved))
        : DEFAULT_TEACH_IN_BRIEF;
    } catch {
      return DEFAULT_TEACH_IN_BRIEF;
    }
  });
  const navigate = useNavigate();

  const updateField =
    (field: keyof TeachInBrief) =>
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setBrief((current) => ({ ...current, [field]: event.target.value }));
    };

  const handleStartFacilitator = () => {
    if (!brief.sessionTitle.trim() || !brief.audience.trim() || !brief.goals.trim()) {
      alert("Add a session title, audience, and goals before starting the facilitator.");
      return;
    }

    localStorage.setItem(TEACH_IN_BRIEF_STORAGE_KEY, JSON.stringify(brief));
    navigate("/facilitator/session");
  };

  return (
    <div className="teach-in-shell">
      <section className="teach-in-intro">
        <div>
          <span className="teach-in-kicker">{EXPERIENCES.facilitator.eyebrow}</span>
          <h1>{EXPERIENCES.facilitator.title}</h1>
          <p>
            Brief Gemini on your audience, context, and Earth Day goals. The
            live facilitator will help you shape a session plan that feels
            grounded, realistic, and action-oriented.
          </p>
        </div>

        <aside className="teach-in-note">
          <h2>Useful contexts</h2>
          <ul>
            {FIELD_NOTES.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </aside>
      </section>

      <section className="teach-in-form-card">
        <div className="teach-in-form-grid">
          <label>
            <span>Session title</span>
            <input
              value={brief.sessionTitle}
              onChange={updateField("sessionTitle")}
              placeholder="Climate action for everyday neighborhoods"
            />
          </label>

          <label>
            <span>Audience</span>
            <input
              value={brief.audience}
              onChange={updateField("audience")}
              placeholder="High-school students, parents, local volunteers"
            />
          </label>

          <label>
            <span>Venue or context</span>
            <input
              value={brief.venueContext}
              onChange={updateField("venueContext")}
              placeholder="School hall, community library, online meetup"
            />
          </label>

          <label>
            <span>Duration</span>
            <input
              value={brief.duration}
              onChange={updateField("duration")}
              placeholder="45 minutes"
            />
          </label>

          <label className="full-width">
            <span>Session goals</span>
            <textarea
              value={brief.goals}
              onChange={updateField("goals")}
              placeholder="Help attendees understand the local climate issue, discuss practical responses, and leave with two actions they can take."
              rows={4}
            />
          </label>

          <label className="full-width">
            <span>Focus areas</span>
            <textarea
              value={brief.focusAreas}
              onChange={updateField("focusAreas")}
              placeholder="Renewable energy, biodiversity, circular economy, climate justice, resilient cities"
              rows={3}
            />
          </label>

          <label className="full-width">
            <span>Available materials</span>
            <textarea
              value={brief.materials}
              onChange={updateField("materials")}
              placeholder="Projector, printouts, whiteboard, local case study, Earth Day toolkit"
              rows={3}
            />
          </label>
        </div>

        <div className="teach-in-actions">
          <button className="teach-in-primary" onClick={handleStartFacilitator}>
            Open live facilitator
          </button>
        </div>
      </section>
    </div>
  );
};

export default ResumeUpload;
